/**
 * @file        src/components/SpecialReturnModal.jsx
 * @description Popup shown when a ROUND TRIP's two fares can't be sold together
 *              because of the Special Return rules (api/flights.js
 *              specialReturnIssue): a Special Return fare on only one leg, on two
 *              different airlines, or two Special Return fares that don't pair.
 *              Raised from the results bar's VIEW PRICES and from the Fare
 *              Options modal's final CONTINUE — before the supplier refuses the
 *              review with errCode 1080. Nothing is changed on the customer's
 *              behalf; the popup says what to fix and the CTA takes them to the
 *              return leg.
 * @author      Kinetika Labs
 * @created     2026-09-14
 * @changelog
 *   - 2026-09-14 Claude — Initial.
 */
import { useEffect } from 'react';

function copyFor(code, airline) {
  if (code === 'BOTH_SPECIAL') {
    return {
      title: 'Both flights should be of Special Return fare',
      body: 'You have chosen a Special Return fare on only one flight. A Special Return fare covers the whole round trip, so please choose a Special Return fare on both your onward and return flights — or a regular fare on both.',
    };
  }
  if (code === 'SAME_AIRLINE') {
    return {
      title: 'Both flights should be of the same airline',
      body: airline
        ? `A Special Return fare is sold by one airline for the whole round trip. Your onward flight is on ${airline}, so please choose a ${airline} Special Return fare for your return flight.`
        : 'A Special Return fare is sold by one airline for the whole round trip, so please choose a Special Return fare on the same airline for your return flight.',
    };
  }
  return {
    title: "These Special Return fares don't match",
    body: 'The airline sells this Special Return fare only together with specific return fares, and the one you chose is not one of them. Please choose a different Special Return fare for your return flight.',
  };
}

/**
 * @param {{ issue: null | { code: string, airline?: string }, ctaLabel?: string, onClose: () => void }} props
 */
export default function SpecialReturnModal({ issue, ctaLabel, onClose }) {
  // Capture phase + stopPropagation: inside the Fare Options modal, Escape should
  // close only this popup, not the modal underneath it.
  useEffect(() => {
    if (!issue) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); onClose?.(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [issue, onClose]);

  if (!issue) return null;
  const { title, body } = copyFor(issue.code, issue.airline);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="alertdialog" aria-modal="true" aria-labelledby="srm-title">
      <div onClick={onClose} className="absolute inset-0 bg-night/85 backdrop-blur-sm" />
      <div className="relative w-full max-w-md rounded-2xl border border-line bg-panel p-6 text-center shadow-2xl">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-400/10 border border-amber-400/40 flex items-center justify-center">
          <svg viewBox="0 0 24 24" className="w-7 h-7 text-amber-400">
            <path fill="currentColor" d="M2.5 19h19a1 1 0 0 0 .87-1.5l-9.5-16a1 1 0 0 0-1.74 0l-9.5 16A1 1 0 0 0 2.5 19zM13 16h-2v-2h2zm0-4h-2V8h2z" />
          </svg>
        </div>
        <h2 id="srm-title" className="font-display text-xl font-extrabold">{title}</h2>
        <p className="text-sm text-muted mt-2">{body}</p>
        <button
          onClick={onClose}
          className="mt-6 w-full py-3 rounded-xl bg-gradient-to-r from-neon to-mint text-onaccent font-bold cursor-pointer hover:brightness-110 transition"
        >
          {ctaLabel || 'Choose return fare'}
        </button>
      </div>
    </div>
  );
}
